import {
  faTag,
  faTags,
  faPlus,
  faPen,
  faTrash,
  faArrowLeft
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import "bootstrap/dist/css/bootstrap.min.css";
import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { useAuthenticatedAPI } from "../hooks/useAuthenticatedAPI";
import ProcessLoading from "../components/processLoading";

const GestionCategoriesMarques = () => {
  const { client, isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const { apiGet, apiPost, apiPut, apiDelete } = useAuthenticatedAPI();
  
  const [categories, setCategories] = useState([]);
  const [marques, setMarques] = useState([]);
  const [activeTab, setActiveTab] = useState('categories');
  const [showModal, setShowModal] = useState(false);
  const [currentItem, setCurrentItem] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [loading, setLoading] = useState(false);
  const [pageLoading, setPageLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  useEffect(() => {
    if (!isAuthenticated()) {
      navigate('/');
      return;
    }

    if (client?.role === 'client') {
      setError('Vous n\'avez pas les autorisations nécessaires');
      setTimeout(() => navigate('/dashboard'), 2000);
      return;
    }

    loadData();
  }, [client, isAuthenticated, navigate]);

  const loadData = async () => {
    setPageLoading(true);
    try {
      await Promise.all([fetchCategories(), fetchMarques()]);
    } catch (err) {
      setError('Erreur lors du chargement des données');
    } finally {
      setPageLoading(false);
    }
  };

  const fetchCategories = async () => {
    try {
      const data = await apiGet('/cat/get');
      setCategories(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error('Erreur fetch categories:', err);
    }
  };

  const fetchMarques = async () => {
    try {
      const data = await apiGet('/marque/get');
      // Ajustement selon la structure de réponse de votre API
      setMarques(Array.isArray(data) ? data : (Array.isArray(data.marques) ? data.marques : []));
    } catch (err) {
      console.error('Erreur fetch marques:', err);
    }
  };

  const handleCreate = (type) => {
    setIsEditing(false);
    setCurrentItem({ nom: '', description: '', type });
    setShowModal(true);
  };

  const handleEdit = (item, type) => {
    setIsEditing(true);
    setCurrentItem({ ...item, type });
    setShowModal(true);
  };

  const handleSave = async () => {
    setLoading(true);
    try {
      const endpoint = currentItem.type === 'categories' ? '/cat' : '/marque';
      
      if (isEditing) {
        // Utilisation des bons IDs selon le type
        const itemId = currentItem.type === 'categories' ? currentItem.id_cat : currentItem.id_marq;
        await apiPut(`${endpoint}/put/${itemId}`, {
          nom: currentItem.nom,
          description: currentItem.description
        });
        setSuccess(`${currentItem.type === 'categories' ? 'Catégorie' : 'Marque'} modifiée avec succès`);
      } else {
        await apiPost(`${endpoint}/create`, {
          nom: currentItem.nom,
          description: currentItem.description
        });
        setSuccess(`${currentItem.type === 'categories' ? 'Catégorie' : 'Marque'} créée avec succès`);
      }

      setShowModal(false);
      await loadData();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.message || 'Erreur lors de la sauvegarde');
      setTimeout(() => setError(''), 5000);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id, type) => {
    if (!window.confirm(`Voulez-vous vraiment supprimer cette ${type === 'categories' ? 'catégorie' : 'marque'} ?`)) {
      return;
    }

    setLoading(true);
    try {
      const endpoint = type === 'categories' ? '/cat' : '/marque';
      await apiDelete(`${endpoint}/del/${id}`);
      setSuccess(`${type === 'categories' ? 'Catégorie' : 'Marque'} supprimée avec succès`);
      await loadData();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.message || 'Erreur lors de la suppression');
      setTimeout(() => setError(''), 5000);
    } finally {
      setLoading(false);
    }
  };

  if (pageLoading) {
    return <ProcessLoading message="Chargement..." />;
  }

  return (
    <div className="container-fluid p-4 bg-light">
      {/* Messages d'erreur et succès */}
      {error && (
        <div className="alert alert-danger alert-dismissible fade show" role="alert">
          {error}
          <button type="button" className="btn-close" onClick={() => setError('')}></button>
        </div>
      )}
      
      {success && (
        <div className="alert alert-success alert-dismissible fade show" role="alert">
          {success}
          <button type="button" className="btn-close" onClick={() => setSuccess('')}></button>
        </div>
      )}

      {/* En-tête */}
      <div className="d-flex justify-content-between align-items-center mb-4">
        <div>
          <h4 className="mb-0">
            <FontAwesomeIcon icon={faTags} className="me-2 text-primary" />
            Gestion des Catégories et Marques
          </h4>
          <small className="text-muted">
            {categories.length} catégorie(s) • {marques.length} marque(s)
          </small>
        </div>
        <button className="btn btn-secondary" onClick={() => navigate('/admin-articles')}>
          <FontAwesomeIcon icon={faArrowLeft} className="me-2" />
          Retour aux Articles
        </button>
      </div>

      {/* Onglets */}
      <div className="card shadow-sm">
        <div className="card-header">
          <ul className="nav nav-tabs card-header-tabs">
            <li className="nav-item">
              <button
                className={`nav-link ${activeTab === 'categories' ? 'active' : ''}`}
                onClick={() => setActiveTab('categories')}
              >
                <FontAwesomeIcon icon={faTag} className="me-2" />
                Catégories ({categories.length})
              </button>
            </li>
            <li className="nav-item">
              <button
                className={`nav-link ${activeTab === 'marques' ? 'active' : ''}`}
                onClick={() => setActiveTab('marques')}
              >
                <FontAwesomeIcon icon={faTags} className="me-2" />
                Marques ({marques.length})
              </button>
            </li>
          </ul>
        </div>

        <div className="card-body">
          {/* Bouton d'ajout */}
          <div className="d-flex justify-content-end mb-3">
            <button
              className="btn btn-primary"
              onClick={() => handleCreate(activeTab)}
            >
              <FontAwesomeIcon icon={faPlus} className="me-2" />
              Ajouter {activeTab === 'categories' ? 'une catégorie' : 'une marque'}
            </button>
          </div>

          {/* Tableau des catégories */}
          {activeTab === 'categories' && (
            <div className="table-responsive">
              <table className="table table-hover">
                <thead className="table-light">
                  <tr>
                    <th>ID</th>
                    <th>Nom de la catégorie</th>
                    <th>Description</th>
                    <th>Date de création</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {categories.map((category) => (
                    <tr key={category.id_cat}>
                      <td className="fw-bold">{category.id_cat}</td>
                      <td>{category.nom}</td>
                      <td className="text-muted">{category.description}</td>
                      <td className="text-muted">
                        {new Date(category.createdAt).toLocaleDateString('fr-FR')}
                      </td>
                      <td>
                        <button
                          className="btn btn-sm btn-outline-primary me-2"
                          onClick={() => handleEdit(category, 'categories')}
                        >
                          <FontAwesomeIcon icon={faPen} />
                        </button>
                        <button
                          className="btn btn-sm btn-outline-danger"
                          onClick={() => handleDelete(category.id_cat, 'categories')}
                        >
                          <FontAwesomeIcon icon={faTrash} />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {categories.length === 0 && (
                <div className="text-center py-4">
                  <p className="text-muted">Aucune catégorie trouvée</p>
                </div>
              )}
            </div>
          )}

          {/* Tableau des marques */}
          {activeTab === 'marques' && (
            <div className="table-responsive">
              <table className="table table-hover">
                <thead className="table-light">
                  <tr>
                    <th>ID</th>
                    <th>Nom de la marque</th>
                    <th>Description</th>
                    <th>Date de création</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {marques.map((marque) => (
                    <tr key={marque.id_marq}>
                      <td className="fw-bold">{marque.id_marq}</td>
                      <td>{marque.nom}</td>
                      <td className="text-muted">{marque.description}</td>
                      <td className="text-muted">
                        {new Date(marque.createdAt).toLocaleDateString('fr-FR')}
                      </td>
                      <td>
                        <button
                          className="btn btn-sm btn-outline-primary me-2"
                          onClick={() => handleEdit(marque, 'marques')}
                        >
                          <FontAwesomeIcon icon={faPen} />
                        </button>
                        <button
                          className="btn btn-sm btn-outline-danger"
                          onClick={() => handleDelete(marque.id_marq, 'marques')}
                        >
                          <FontAwesomeIcon icon={faTrash} />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {marques.length === 0 && (
                <div className="text-center py-4">
                  <p className="text-muted">Aucune marque trouvée</p>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Modal pour ajouter/modifier */}
      {showModal && (
        <div className="modal fade show d-block" tabIndex="-1" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">
                  {isEditing ? 'Modifier' : 'Ajouter'} {currentItem?.type === 'categories' ? 'une catégorie' : 'une marque'}
                </h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={() => setShowModal(false)}
                ></button>
              </div>
              <div className="modal-body">
                <div className="mb-3">
                  <label className="form-label">
                    Nom {currentItem?.type === 'categories' ? 'de la catégorie' : 'de la marque'} *
                  </label>
                  <input
                    type="text"
                    className="form-control"
                    value={currentItem?.nom || ''}
                    onChange={(e) => setCurrentItem({...currentItem, nom: e.target.value})}
                    required
                  />
                </div>
                <div className="mb-3">
                  <label className="form-label">
                    Description *
                  </label>
                  <textarea
                    className="form-control"
                    rows="3"
                    value={currentItem?.description || ''}
                    onChange={(e) => setCurrentItem({...currentItem, description: e.target.value})}
                    placeholder={`Décrivez cette ${currentItem?.type === 'categories' ? 'catégorie' : 'marque'}...`}
                    required
                  />
                </div>
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => setShowModal(false)}
                  disabled={loading}
                >
                  Annuler
                </button>
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={handleSave}
                  disabled={loading || !currentItem?.nom?.trim() || !currentItem?.description?.trim()}
                >
                  {loading ? (
                    <>
                      <span className="spinner-border spinner-border-sm me-2" />
                      {isEditing ? 'Modification...' : 'Création...'}
                    </>
                  ) : (
                    isEditing ? 'Enregistrer' : 'Ajouter'
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {loading && <ProcessLoading message="Traitement en cours..." />}
    </div>
  );
};

export default GestionCategoriesMarques;